import { Alert, Stack, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'

import { MonitorSearchField } from 'components/status-checks/form/status-check/common/MonitorSearchField'
import {
  type MonitorListGridRow,
  MonitorSearchList
} from 'components/status-checks/form/status-check/common/MonitorSearchList'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { messageFromResponseError } from 'utils/strings'

import type { DatadogMonitorsResponse } from 'types/api/datadog/monitors'

// for now we're limiting it to displaying only 50 monitors
// this is somewhat arbitrary - the intent is to get the user to narrow down their search
// we want to avoid having to add complex pagination-handling logic here at first
const MAX_MONITORS_RETRIEVED = 50

export const DatadogMonitorSearch: React.FC<{
  disabled?: boolean
  domain: string
  initialSearchValue?: string
  integrationName: string
  selectedValue: string | undefined
  onSelect(monitorId: string): void
}> = ({
  disabled,
  domain,
  initialSearchValue = '',
  integrationName,
  selectedValue,
  onSelect
}) => {
  const { currentTeamId } = useAuth()
  const [queryValue, setQueryValue] = useState(initialSearchValue ?? '')

  const { data, isError, isFetching, isIdle, isSuccess, error } =
    useGremlinQuery<DatadogMonitorsResponse>(
      `/datadog/monitors?teamId=${currentTeamId}&domain=${domain}&searchString=${queryValue}&page=0&perPage=${MAX_MONITORS_RETRIEVED}&datadogIntegrationName=${integrationName}`,
      {},
      { enabled: Boolean(queryValue) }
    )

  /*
   * Datadog monitor search rows return a different data model than Grafana/Prometheus endpoints, and to
   * normalize how rows are rendered, we transform the result to map the name and id field values.
   */
  const monitorRowData = useMemo<MonitorListGridRow[] | undefined>(
    () =>
      data?.monitors?.map(({ id, name, tags }) => ({
        labels: tags,
        title: name,
        uid: `${id ?? name}`
      })),
    [data]
  )

  const totalMonitorCounts = isSuccess ? data?.metadata.total_count : 0

  return (
    <Stack spacing={2}>
      <MonitorSearchField
        disabled={disabled}
        initialValue={initialSearchValue}
        isLoading={isFetching}
        onSubmit={setQueryValue}
      />
      {queryValue && !isFetching && !disabled && (
        <>
          {isError ? (
            <Alert severity="error">
              {messageFromResponseError(error, 'Monitor search failed')}
            </Alert>
          ) : (
            !isIdle && (
              <MonitorSearchList
                isLoading={isFetching}
                rows={monitorRowData ?? []}
                selectedRowId={selectedValue}
                onClick={(monitorId) => {
                  onSelect(monitorId)
                }}
              />
            )
          )}
          {totalMonitorCounts > MAX_MONITORS_RETRIEVED && (
            <Typography variant="body2">
              {`Found ${totalMonitorCounts} monitors. Showing first ${MAX_MONITORS_RETRIEVED}`}
            </Typography>
          )}
        </>
      )}
    </Stack>
  )
}
